<template>
  <div ref="root" class="relative inline-flex">
    <slot name="trigger" :open="open" :toggle="toggle" />
  </div>

  <Teleport to="body">
    <div
      v-if="open"
      ref="panel"
      :data-testid="panelTestId"
      :class="['conference-text-menu fixed z-30 max-w-[calc(100vw-1rem)] overflow-hidden rounded-[1px]', toneClass, widthClass]"
      :style="panelStyle"
      role="menu"
      @keydown="handlePanelKeyDown"
    >
      <slot :close="close" />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type CSSProperties } from 'vue';

const open = defineModel<boolean>('open', {
  default: false,
});

const props = withDefaults(
  defineProps<{
    align?: 'left' | 'right';
    panelTestId?: string;
    tone?: 'chrome' | 'light';
    width?: 'lg' | 'md' | 'panel' | 'sm';
  }>(),
  {
    align: 'right',
    panelTestId: undefined,
    tone: 'light',
    width: 'md',
  },
);

const root = ref<HTMLElement | null>(null);
const panel = ref<HTMLElement | null>(null);
const panelStyle = ref<CSSProperties>({});

const focusableSelector =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="menuitem"]:not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"])';

const widthClass = computed(() => {
  switch (props.width) {
    case 'sm':
      return 'min-w-36';
    case 'lg':
      return 'min-w-56';
    case 'panel':
      return 'w-46.5';
    default:
      return 'min-w-40';
  }
});

const toneClass = computed(() => {
  switch (props.tone) {
    case 'chrome':
      return 'border border-[#2a2a2a] bg-conference-structure p-px text-white shadow-[0_1px_5px_rgba(0,0,0,0.26)]';
    default:
      return 'border border-conference-border bg-white p-0 text-[#333333] shadow-[0_0_8px_rgba(0,0,0,0.18)]';
  }
});

function close(): void {
  open.value = false;
}

function toggle(): void {
  open.value = !open.value;
}

function getFocusableItems(): HTMLElement[] {
  return Array.from(panel.value?.querySelectorAll<HTMLElement>(focusableSelector) ?? []);
}

function focusMenuItem(index: number): void {
  const items = getFocusableItems();

  if (items.length === 0) {
    return;
  }

  const wrappedIndex = (index + items.length) % items.length;
  items[wrappedIndex]?.focus();
}

function handlePointerDown(event: MouseEvent): void {
  const target = event.target as Node;

  if (!root.value?.contains(target) && !panel.value?.contains(target)) {
    close();
  }
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    close();
  }
}

function handlePanelKeyDown(event: KeyboardEvent): void {
  if (!panel.value?.contains(event.target as Node)) {
    return;
  }

  const items = getFocusableItems();

  if (items.length === 0) {
    return;
  }

  const currentIndex = items.findIndex((item) => item === document.activeElement);

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusMenuItem(currentIndex < 0 ? 0 : currentIndex + 1);
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    focusMenuItem(currentIndex < 0 ? items.length - 1 : currentIndex - 1);
    return;
  }

  if (event.key === 'Home') {
    event.preventDefault();
    focusMenuItem(0);
    return;
  }

  if (event.key === 'End') {
    event.preventDefault();
    focusMenuItem(items.length - 1);
  }
}

function updatePanelPosition(): void {
  if (!open.value || !root.value) {
    return;
  }

  const triggerRect = root.value.getBoundingClientRect();
  const viewportPadding = 8;
  const top = Math.min(triggerRect.bottom + 1, window.innerHeight - viewportPadding);

  panelStyle.value =
    props.align === 'right'
      ? {
          top: `${top}px`,
          right: `${Math.max(viewportPadding, window.innerWidth - triggerRect.right)}px`,
        }
      : {
          top: `${top}px`,
          left: `${Math.max(viewportPadding, triggerRect.left)}px`,
        };
}

function handleViewportChange(): void {
  updatePanelPosition();
}

watch(open, async (value) => {
  if (!value) {
    window.removeEventListener('resize', handleViewportChange);
    window.removeEventListener('scroll', handleViewportChange, true);
    return;
  }

  await nextTick();
  updatePanelPosition();
  window.addEventListener('resize', handleViewportChange);
  window.addEventListener('scroll', handleViewportChange, true);
  focusMenuItem(0);
});

onMounted(() => {
  document.addEventListener('mousedown', handlePointerDown);
  document.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handlePointerDown);
  document.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('resize', handleViewportChange);
  window.removeEventListener('scroll', handleViewportChange, true);
});
</script>
